import { z } from "zod";
import { addDays, addMonths, daysBetween } from "./obligations.js";
import type { ObligationKind } from "./library.js";
import type { RecordResult } from "./obligations.js";

/**
 * Operationele planning (kruisjesplanning, planning stap 2): wat moet wanneer gebeuren, over alle gebouwen.
 * Status en momenten worden berekend, nooit opgeslagen (zoals bij keuringen).
 */
export const PLANNING_KINDS = ["keuring", "inspectie", "controle", "onderhoud", "overig", "taak", "meter"] as const;
export type PlanningKind = (typeof PLANNING_KINDS)[number];
export const PLANNING_KIND_LABELS: Record<PlanningKind, string> = {
  keuring: "Keuring",
  inspectie: "Inspectie",
  controle: "Controle",
  onderhoud: "Onderhoud",
  overig: "Overig",
  taak: "Taak",
  meter: "Meterronde",
};

/** Volgorde = sortering in de planning (eerst wat aandacht vraagt). */
export const PLANNING_STATUSES = [
  "verlopen",
  "na_vervaldatum",
  "te_plannen",
  "gepland",
  "later",
  "onbekend",
  "klaar",
] as const;
export type PlanningStatus = (typeof PLANNING_STATUSES)[number];
export const PLANNING_STATUS_LABELS: Record<PlanningStatus, string> = {
  verlopen: "Verlopen",
  na_vervaldatum: "Gepland na vervaldatum",
  te_plannen: "Te plannen",
  gepland: "Gepland",
  later: "Later",
  onbekend: "Datum onbekend",
  klaar: "Klaar",
};

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Datum als JJJJ-MM-DD");
export const planningQuerySchema = z
  .object({ from: isoDate, to: isoDate, buildingId: z.uuid().optional() })
  .refine((q) => q.from <= q.to && daysBetween(q.from, q.to) <= 400, { message: "Periode van hooguit een jaar" });
export type PlanningQuery = z.infer<typeof planningQuerySchema>;

export const PLANNING_SOURCES = ["obligation", "task", "meter"] as const;
export type PlanningSource = (typeof PLANNING_SOURCES)[number];
export const APPOINTMENT_MAX_DAYS = 30;

/** Afspraak inplannen of verplaatsen (planning stap 3). Eén afspraak per regel: een nieuwe vervangt de oude. */
export const appointmentSchema = z.object({
  source: z.enum(PLANNING_SOURCES),
  refId: z.uuid(),
  startOn: isoDate,
  days: z.number().int().min(1).max(APPOINTMENT_MAX_DAYS).default(1),
  confirmed: z.boolean().default(false),
  /** Uitvoerder uit het adresboek, of vrije tekst. */
  performerCompanyId: z.uuid().nullish(),
  performerPersonId: z.uuid().nullish(),
  performer: z.string().trim().max(200).nullish(),
  note: z.string().trim().max(2000).nullish(),
});
export type AppointmentInput = z.input<typeof appointmentSchema>;

export interface PlanningAppointmentDto {
  id: string;
  startOn: string;
  /** Laatste dag (tot en met). */
  endOn: string;
  days: number;
  /** Bevestigd door de uitvoerder; anders voorlopig. */
  confirmed: boolean;
  /** Voor een klant alleen het bedrijf. */
  performer: { companyId: string | null; personId: string | null; name: string } | null;
  note: string | null;
}

/** Eén rij in de planning: een uitvoering bij een asset, een taak of een meterronde. */
export interface PlanningLineDto {
  /** Uniek over de bronnen: `o:<id>`, `t:<id>` of `m:<id>`. */
  id: string;
  source: "obligation" | "task" | "meter";
  /** ID in de bron (verplichting, taak of meterronde). */
  refId: string;
  kind: PlanningKind;
  title: string;
  /** Taak: de code (T-0012). */
  code: string | null;
  asset: { id: string; code: string; name: string } | null;
  building: { id: string; code: string; name: string };
  clientName: string | null;
  /** Uitvoerder; voor een klant alleen het bedrijf, zonder namen van monteurs. */
  performer: { companyId: string | null; personId: string | null; name: string } | null;
  /** Wettelijk verplicht. */
  legal: boolean;
  intervalMonths: number | null;
  intervalWeeks: number | null;
  endYear: number | null;
  /** Vervaldatum, uiterlijk-datum van de taak of einde van de meterperiode. */
  dueOn: string | null;
  /** Laatste uitkomst: na een afkeuring is de regel verlopen. */
  lastResult: RecordResult | null;
  /** Uitgevoerd op (binnen de gevraagde periode). */
  done: string[];
  /** Taak klaar of meterronde afgerond: geen volgende keer. */
  closed: boolean;
  /** Ingeplande afspraak; vervalt zodra er daarna een uitvoering is vastgelegd. */
  appointment: PlanningAppointmentDto | null;
  /** Mag deze gebruiker de regel inplannen en verplaatsen. */
  canPlan: boolean;
}

export interface PlanningResponse {
  from: string;
  to: string;
  lines: PlanningLineDto[];
}

/**
 * Termijn voor *te plannen* (besluit 23-10): keuringen en wettelijk werk 60 dagen, kwartaalwerk 21,
 * maandwerk 7, wekelijks 2; taken en meterrondes 7.
 */
export function leadDays(l: Pick<PlanningLineDto, "kind" | "legal" | "intervalMonths" | "intervalWeeks">): number {
  if (l.kind === "taak" || l.kind === "meter") return 7;
  if (l.kind === "keuring" || l.legal) return 60;
  if (l.intervalWeeks) return l.intervalWeeks <= 1 ? 2 : 7;
  if (l.intervalMonths != null && l.intervalMonths <= 1) return 7;
  if (l.intervalMonths != null && l.intervalMonths <= 3) return 21;
  return 60;
}

/**
 * Status van een regel. Met een afspraak die nog niet voorbij is: *gepland*, of *gepland na vervaldatum* als de
 * afspraak pas na de vervaldatum klaar is. Is de afspraak voorbij zonder uitvoering, dan telt weer de vervaldatum.
 */
export function planningStatus(l: PlanningLineDto, today: string): PlanningStatus {
  if (l.closed) return "klaar";
  const a = l.appointment;
  if (a && a.endOn >= today)
    return l.dueOn && a.endOn > l.dueOn && l.lastResult !== "afgekeurd" ? "na_vervaldatum" : "gepland";
  if (l.lastResult === "afgekeurd") return "verlopen";
  if (!l.dueOn) return "onbekend";
  const d = daysBetween(today, l.dueOn);
  if (d < 0) return "verlopen";
  return d <= leadDays(l) ? "te_plannen" : "later";
}

export type PlanningMomentType = "done" | "due" | "forecast" | "planned";
export interface PlanningMoment {
  date: string;
  type: PlanningMomentType;
}

const MAX_FORECASTS = 400;

/** Uitgevoerd (✓), volgende keer (✕), prognose (licht kruis) en de afspraak binnen `from`–`to`, op datum. */
export function planningMoments(l: PlanningLineDto, from: string, to: string): PlanningMoment[] {
  const out: PlanningMoment[] = l.done.filter((d) => d >= from && d <= to).map((date) => ({ date, type: "done" }));
  const a = l.appointment;
  if (a && !l.closed && a.endOn >= from && a.startOn <= to)
    out.push({ date: a.startOn < from ? from : a.startOn, type: "planned" });
  if (!l.closed && l.dueOn) {
    if (l.dueOn >= from && l.dueOn <= to) out.push({ date: l.dueOn, type: "due" });
    // Vanaf de vervaldatum gerekend, zodat 31 jan + n maanden niet wegschuift naar de 28e.
    let n = 1;
    for (;;) {
      const next = l.intervalWeeks
        ? addDays(l.dueOn, 7 * l.intervalWeeks * n)
        : l.intervalMonths
          ? addMonths(l.dueOn, l.intervalMonths * n)
          : null;
      if (!next || next > to || n > MAX_FORECASTS) break;
      if (l.endYear && Number(next.slice(0, 4)) > l.endYear) break;
      if (next >= from) out.push({ date: next, type: "forecast" });
      n++;
    }
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

// ── Tijdas ──

const MONTHS_SHORT = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const MONTHS_LONG = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];
const WEEKDAYS = ["ma", "di", "wo", "do", "vr", "za", "zo"];

/** Dag van de week, maandag = 0. */
export const weekdayIndex = (iso: string) => (new Date(`${iso}T12:00:00Z`).getUTCDay() + 6) % 7;
export const mondayOf = (iso: string) => addDays(iso, -weekdayIndex(iso));

/** ISO-weeknummer en -jaar (week 1 bevat 4 januari; sommige jaren hebben week 53). */
export function isoWeek(iso: string): { year: number; week: number } {
  const thursday = addDays(iso, 3 - weekdayIndex(iso));
  const year = Number(thursday.slice(0, 4));
  return { year, week: Math.floor(daysBetween(`${year}-01-01`, thursday) / 7) + 1 };
}

/** Maandag van week 1 van een ISO-jaar. */
export const isoYearStart = (year: number) => mondayOf(`${year}-01-04`);

export type PlanningView = "jaar" | "maand" | "week";
export interface PlanningColumn {
  /** Eerste en laatste dag (tot en met). */
  start: string;
  end: string;
  /** Weeknummer of dag van de maand. */
  label: string;
  /** Dag van de week (maand- en weekweergave). */
  sub: string | null;
  /** Groep erboven: maand (jaar), week (maand) of de week zelf. */
  group: string;
  weekend: boolean;
}

export interface PlanningPeriod {
  view: PlanningView;
  from: string;
  to: string;
  title: string;
  columns: PlanningColumn[];
}

/** Kolommen van een weergave rond `anchor`: jaar = de ISO-weken, maand = de dagen, week = maandag t/m zondag. */
export function planningPeriod(view: PlanningView, anchor: string): PlanningPeriod {
  if (view === "jaar") {
    const year = isoWeek(anchor).year;
    const first = isoYearStart(year);
    const last = addDays(isoYearStart(year + 1), -1);
    const columns: PlanningColumn[] = [];
    for (let d = first; d <= last; d = addDays(d, 7)) {
      // Maand van de donderdag, zoals het weeknummer.
      const thu = addDays(d, 3);
      columns.push({
        start: d,
        end: addDays(d, 6),
        label: String(isoWeek(d).week),
        sub: null,
        group: MONTHS_LONG[Number(thu.slice(5, 7)) - 1]!,
        weekend: false,
      });
    }
    return { view, from: first, to: last, title: String(year), columns };
  }
  if (view === "maand") {
    const first = `${anchor.slice(0, 7)}-01`;
    const last = addDays(addMonths(first, 1), -1);
    const columns: PlanningColumn[] = [];
    for (let d = first; d <= last; d = addDays(d, 1)) {
      const wd = weekdayIndex(d);
      columns.push({
        start: d,
        end: d,
        label: String(Number(d.slice(8, 10))),
        sub: WEEKDAYS[wd]!,
        group: `Week ${isoWeek(d).week}`,
        weekend: wd >= 5,
      });
    }
    const m = Number(first.slice(5, 7)) - 1;
    return { view, from: first, to: last, title: `${MONTHS_LONG[m]} ${first.slice(0, 4)}`, columns };
  }
  const first = mondayOf(anchor);
  const last = addDays(first, 6);
  const w = isoWeek(first);
  const columns: PlanningColumn[] = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(first, i);
    return {
      start: d,
      end: d,
      label: `${Number(d.slice(8, 10))} ${MONTHS_SHORT[Number(d.slice(5, 7)) - 1]}`,
      sub: WEEKDAYS[i]!,
      group: `Week ${w.week}`,
      weekend: i >= 5,
    };
  });
  return { view, from: first, to: last, title: `Week ${w.week}, ${w.year}`, columns };
}

/** Volgende of vorige periode. */
export function shiftAnchor(view: PlanningView, anchor: string, by: number): string {
  if (view === "jaar") return `${isoWeek(anchor).year + by}-07-01`;
  if (view === "maand") return addMonths(`${anchor.slice(0, 7)}-01`, by);
  return addDays(anchor, 7 * by);
}

/** Korte datum: "3 nov 2026". */
export const planningDay = (iso: string) =>
  `${Number(iso.slice(8, 10))} ${MONTHS_SHORT[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;

export const isObligationKind = (k: PlanningKind): k is ObligationKind => k !== "taak" && k !== "meter";
